import { isRetiredFinding } from '../lib/scoring';
import { matchingAck, ackEvidence, isResolvedAck } from '../lib/findingAck';
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CATEGORY_BADGE_LABELS, CATEGORY_BADGE_COLORS } from '@engine/index';
import { t, getCurrentLang } from '../lib/i18n';
import Icon from '../components/Icon';
import { getAckStore } from '../lib/ackStore';
import { useAppContext } from '../lib/AppContext';
import { authFetch } from '../lib/authFetch';
import EvidenceTable from './EvidenceTable';
import VatPeriodTable from './VatPeriodTable';
import AmortizationTable from './AmortizationTable';
import { getActionText, getIssueDesc, getIssueTitle, localizedIssue } from './issueText';

/**
 * IssueCard — Single audit finding card with severity icon, title, description,
 * evidence table, action recommendation, and ack buttons (Sjekket/Fikset/Undo).
 *
 * Extracted from index.html (previously inline ~lines 801-938).
 *
 * @param {object} props
 *   - issue: audit finding object from engine
 *   - globalExpand: boolean | null — controls expand/collapse from parent
 *   - ackStatus: { id, rule_id, action } | null — current ack state
 *   - onAck: (ruleId, action) => void — callback for ack submission
 *   - onUndoAck: (ackId) => void — callback for ack removal
 *   - clientId: string — client identifier for ack storage
 */
function ReportIssueCard({ issue, globalExpand, ackStatus, onAck, onUndoAck, clientId: cardClientId }) {
    const card = localizedIssue(issue);
    const hasVatTerms = issue.ruleId === 'mva_period_check' && Array.isArray(issue.evidence?.terms) && issue.evidence.terms.length > 0
        && issue.evidence.terms.every(row => Array.isArray(row?.accountBalances) && Object.hasOwn(row, 'ledgerCleared'));
    const [localExpanded, setLocalExpanded] = useState(false);
    const [localAck, setLocalAck] = useState(null);
    // P0 fix (2026-04-27): Permanent ignore — track scope dla badge styling
    const [localAckScope, setLocalAckScope] = useState('period');
    // P1 fix (2026-04-25): Notes modal state — dla critical errors required, dla warning optional
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pendingScope, setPendingScope] = useState('period'); // P0 fix 2026-04-27
    const [noteValue, setNoteValue] = useState('');
    const [ackSubmitting, setAckSubmitting] = useState(false);
    const _appCtx = useAppContext();
    const ackPeriod = _appCtx && Object.hasOwn(_appCtx, 'auditPeriod') ? _appCtx.auditPeriod : _appCtx?.batchPeriod;
    const evidence = ackEvidence(issue, ackPeriod);
    const readClientId = cardClientId || _appCtx?.ackClientId;
    const canAcknowledge = !!(readClientId && ackPeriod?.year && ackPeriod?.monthTo && evidence.evidence_key);
    ackStatus = canAcknowledge ? matchingAck([...(getAckStore()[readClientId] || []), ...(ackStatus ? [ackStatus] : [])], issue, ackPeriod) : null;

    useEffect(() => {
        if (!showNoteModal || typeof document === 'undefined') return undefined;
        const previous = document.body.dataset.auditAckModalOpen;
        document.body.dataset.auditAckModalOpen = 'true';
        return () => {
            if (previous === undefined) {
                delete document.body.dataset.auditAckModalOpen;
            } else {
                document.body.dataset.auditAckModalOpen = previous;
            }
        };
    }, [showNoteModal]);

    useEffect(() => {
        if (globalExpand !== null) setLocalExpanded(globalExpand);
    }, [globalExpand]);

    // Sync external ack status to local state (from prop OR module store)
    useEffect(() => {
        if (ackStatus) {
            setLocalAck(ackStatus.action);
            setLocalAckScope(ackStatus.ack_scope || 'period');
            return;
        }
        // Fallback: check module-level _ackStore directly
        const _ackStore = getAckStore();
        const cid = cardClientId || (_appCtx && _appCtx.ackClientId) || '';
        if (canAcknowledge && cid && issue.ruleId && _ackStore[cid]) {
            const found = matchingAck(_ackStore[cid], issue, ackPeriod);
            if (found) {
                setLocalAck(found.action);
                setLocalAckScope(found.ack_scope || 'period');
                return;
            }
        }
        setLocalAck(null);
        setLocalAckScope('period');
    }, [ackStatus, cardClientId, issue, evidence.evidence_year, evidence.evidence_month]);

    // P1 fix (2026-04-25): wrapper żeby dla error type wymagać notes (audit trail)
    // P1 fix (2026-04-26): previous note hint z poprzedniego ack tej samej rule+client
    const [autoFillSource, setAutoFillSource] = useState(null);
    const [autoFillNote, setAutoFillNote] = useState('');
    // P0 fix (2026-04-26): history flag — eliminuje déjà vu dla recurring errors
    const [historyFlag, setHistoryFlag] = useState(null);
    useEffect(() => {
        if (ackStatus) return; // already acked — skip history check
        const cid = cardClientId || (_appCtx && _appCtx.ackClientId) || '';
        if (!cid || !issue.ruleId) return;
        const py = (_appCtx?.auditPeriod || _appCtx?.batchPeriod || {}).year || new Date().getFullYear();
        const pm = (_appCtx?.auditPeriod || _appCtx?.batchPeriod || {}).monthTo || (new Date().getMonth() + 1);
        let cancelled = false;
        (async () => {
            try {
                const r = await authFetch(`/api/audit-ack/history?client_id=${encodeURIComponent(cid)}&rule_id=${encodeURIComponent(issue.ruleId)}&exclude_year=${py}&exclude_month=${pm}`);
                if (!r.ok || cancelled) return;
                const data = await r.json();
                if (data.history && data.history.length > 0) {
                    setHistoryFlag({ lastPeriod: data.history[0].period, lastAction: data.history[0].action, count: data.count });
                }
            } catch (e) { /* network fail = no badge, OK */ }
        })();
        return () => { cancelled = true; };
    }, [ackStatus, cardClientId, issue, evidence.evidence_year, evidence.evidence_month]);
    // P0 fix (2026-04-27): handleAckClick przyjmuje scope = 'period' | 'permanent'
    // Permanent ZAWSZE wymusza modal z notes >=10 chars, niezależnie od issue.type.
    const handleAckClick = async (action, scope = 'period') => {
        if (!canAcknowledge) return;
        const requiresModal = true;
        if (requiresModal) {
            setPendingAction(action);
            setPendingScope(scope);
            setNoteValue('');
            setAutoFillSource(null);
            setAutoFillNote('');
            setShowNoteModal(true);
            // Fetch previous note dla tej rule+client (cross-period), but do not prefill:
            // old historical notes can be wrong for the current topic and must be consciously inserted.
            const cid = cardClientId || (_appCtx && _appCtx.ackClientId) || '';
            if (cid && issue.ruleId) {
                try {
                    const r = await authFetch(`/api/audit-ack/last-note?client_id=${encodeURIComponent(cid)}&rule_id=${encodeURIComponent(issue.ruleId)}`);
                    if (r.ok) {
                        const data = await r.json();
                        if (data.note) {
                            setAutoFillSource(data.period);
                            setAutoFillNote(data.note);
                        }
                    }
                } catch (e) { /* network fail = empty default, OK */ }
            }
        } else {
            handleAck(action, '', scope);
        }
    };

    // P0 fix (2026-04-27): handleAck przyjmuje scope. Permanent: ack_scope='permanent' w POST body.
    // P0 fix (2026-05-19): error handling + rollback — user musi wiedzieć gdy zapis się nie udał (network/server).
    const handleAck = async (action, notes, scope = 'period') => {
        if (!canAcknowledge) return;
        if (ackSubmitting) return;
        if (!evidence.evidence_key || !evidence.evidence_year || !evidence.evidence_month) {
            window.alert(t(!evidence.evidence_year || !evidence.evidence_month ? 'report_period_unknown' : 'ack_evidence_missing'));
            return;
        }
        setAckSubmitting(true);
        const prevAck = localAck;
        const prevScope = localAckScope;
        setLocalAck(action);
        setLocalAckScope(scope);
        const _ackStore = getAckStore();
        const cid = cardClientId || (_appCtx && _appCtx.ackClientId) || '';
        const prevStoreEntry = (cid && issue.ruleId) ? matchingAck(_ackStore[cid], issue, _appCtx?.auditPeriod || _appCtx?.batchPeriod) : null;
        // Update module store for cross-view sync (upsert)
        if (cid && issue.ruleId) {
            _ackStore[cid] = [...(_ackStore[cid] || []).filter(a => !(a.rule_id === issue.ruleId && a.evidence_key === evidence.evidence_key)), { id: Date.now(), rule_id: issue.ruleId, action, notes, ack_scope: scope, ...evidence }];
        }
        const rollback = (reason) => {
            setLocalAck(prevAck);
            setLocalAckScope(prevScope);
            if (cid && issue.ruleId) {
                _ackStore[cid] = (_ackStore[cid] || []).filter(a => !(a.rule_id === issue.ruleId && a.evidence_key === evidence.evidence_key));
                if (prevStoreEntry) _ackStore[cid].push(prevStoreEntry);
            }
            setAckSubmitting(false);
            try { window.alert((t('ack_save_failed') || 'Ikke lagret — prøv igjen') + (reason ? ': ' + reason : '')); } catch(_) {}
        };
        if (onAck) {
            try {
                await onAck(issue.ruleId, action, notes, scope, evidence);
            } catch (e) {
                rollback(e && e.message ? e.message : 'network');
                return;
            }
        }
        else if (cid && issue.ruleId) {
            try {
                const body = { client_id: cid, rule_id: issue.ruleId, action, notes: notes || null, ack_scope: scope, ...evidence };
                if (scope === 'period') {
                    body.period_year = (_appCtx.auditPeriod || _appCtx.batchPeriod || {}).year || new Date().getFullYear();
                    body.period_month = (_appCtx.auditPeriod || _appCtx.batchPeriod || {}).monthTo || (new Date().getMonth() + 1);
                }
                const resp = await authFetch('/api/audit-ack', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                });
                if (!resp || !resp.ok) {
                    const status = resp ? resp.status : 0;
                    let detail = '';
                    try { const j = await resp.json(); detail = j.error || j.detail || ''; } catch(_) {}
                    rollback('HTTP ' + status + (detail ? ' — ' + detail : ''));
                    return;
                }
                if ((await resp.json()).ok !== true) throw new Error(t('toast_ack_network_error'));
                const read = await authFetch(`/api/audit-ack?client=${encodeURIComponent(cid)}&year=${evidence.evidence_year}&month=${evidence.evidence_month}`);
                if (!read.ok) throw new Error(t('toast_ack_network_error'));
                _ackStore[cid] = (await read.json()).acks || [];
            } catch (e) {
                rollback(e && e.message ? e.message : 'network');
                return;
            }
        }
        setShowNoteModal(false);
        setPendingAction(null);
        setPendingScope('period');
        setAckSubmitting(false);
    };

    const handleUndoAck = async () => {
        if (!canAcknowledge) return;
        const prevAck = localAck;
        const prevScope = localAckScope;
        setLocalAck(null);
        setLocalAckScope('period');
        const _ackStore = getAckStore();
        const cid = cardClientId || (_appCtx && _appCtx.ackClientId) || '';
        const prevStoreEntry = (cid && issue.ruleId) ? matchingAck(_ackStore[cid], issue, _appCtx?.auditPeriod || _appCtx?.batchPeriod) : null;
        if (cid && issue.ruleId) {
            _ackStore[cid] = (_ackStore[cid] || []).filter(a => !(a.rule_id === issue.ruleId && a.evidence_key === evidence.evidence_key));
        }
        const rollback = (reason) => {
            setLocalAck(prevAck);
            setLocalAckScope(prevScope);
            if (cid && issue.ruleId && prevStoreEntry) {
                _ackStore[cid] = [...(_ackStore[cid] || []), prevStoreEntry];
            }
            try { window.alert((t('ack_undo_failed') || 'Angre mislyktes — prøv igjen') + (reason ? ': ' + reason : '')); } catch(_) {}
        };
        if (onUndoAck && ackStatus) {
            try { await onUndoAck(ackStatus.id); } catch (e) { rollback(e.message); }
        }
        else if (ackStatus && ackStatus.id && cid) {
            try {
                const resp = await authFetch(`/api/audit-ack/${ackStatus.id}`, { method: 'DELETE' });
                if (!resp || !resp.ok) {
                    rollback('HTTP ' + (resp ? resp.status : 0));
                    return;
                }
            } catch (e) {
                rollback(e && e.message ? e.message : 'network');
                return;
            }
        }
    };

    const styles = {
        error: 'border-l-4 border-l-red-500 bg-red-50 dark:bg-red-900/20 dark:border-l-red-500',
        warning: 'border-l-4 border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 dark:border-l-amber-500',
        info: 'border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-l-blue-500',
        success: 'border-l-4 border-l-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-l-emerald-500',
    };
    const icons = { error: 'alert-octagon', warning: 'alert-triangle', info: 'info', success: 'check-circle-2' };
    const titles = { error: 'text-red-800 dark:text-red-300', warning: 'text-amber-800 dark:text-amber-300', info: 'text-blue-800 dark:text-blue-300', success: 'text-emerald-800 dark:text-emerald-300' };

    const isAcked = isResolvedAck({ action: localAck });
    const ackActor = ackStatus ? (ackStatus.accountant_name || ackStatus.user_email || '') : '';
    const ackTimestamp = ackStatus && ackStatus.created_at
        ? new Date(ackStatus.created_at * 1000).toLocaleString('nb-NO', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
        : '';
    const getAckActionLabel = (action, scope = localAckScope) => {
        if (scope === 'permanent') return t('ack_permanent_badge');
        if (action === 'client_followup') return t('ack_client_followup');
        return action === 'checked' ? t('ack_checked') : t('ack_fixed');
    };
    const ackActionLabel = getAckActionLabel(localAck);
    const ackStyle = isAcked ? {
        opacity: 0.35,
        outline: '3px solid #34d399',
        outlineOffset: '-2px',
        background: localAck === 'checked' ? '#d1fae5' : (localAck === 'client_followup' ? '#fef3c7' : '#dbeafe'),
        transition: 'all 0.3s ease',
    } : { transition: 'all 0.3s ease' };
    const noteModal = showNoteModal && typeof document !== 'undefined'
        ? createPortal((
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md mx-4 p-6" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-start gap-3 mb-4">
                        <Icon name={pendingScope === 'permanent' ? 'clipboard-check' : (pendingAction === 'client_followup' ? 'message-square' : 'alert-octagon')} className={`w-6 h-6 flex-shrink-0 ${pendingScope === 'permanent' ? 'text-orange-500' : (pendingAction === 'client_followup' ? 'text-amber-500' : 'text-red-500')}`} />
                        <div>
                            <h3 className="font-bold text-lg dark:text-white">{pendingScope === 'permanent' ? t('ack_permanent_modal_title') : (pendingAction === 'client_followup' ? t('ack_client_followup') : t('ack_why_question'))}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{getIssueTitle(issue)}</p>
                        </div>
                    </div>
                    {pendingScope === 'permanent' && (
                        <div className="mb-3 px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 text-xs text-orange-800 dark:text-orange-200 flex items-start gap-2">
                            <Icon name="alert-triangle" className="w-4 h-4 flex-shrink-0 mt-0.5 text-orange-600 dark:text-orange-400" />
                            <div>
                                <div className="font-semibold mb-0.5">{t('ack_permanent_warning_title')}</div>
                                <div>{t('ack_permanent_warning_body')}</div>
                            </div>
                        </div>
                    )}
                    {autoFillSource && autoFillNote && (
                        <div className="mb-2 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 text-xs text-blue-700 dark:text-blue-300">
                            <div className="flex items-start gap-2">
                                <Icon name="info" className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                    <div><span>{t('ack_autofill_from')} <strong>{autoFillSource}</strong> - {t('ack_autofill_hint')}</span></div>
                                    <div className="mt-1 whitespace-pre-wrap break-words text-blue-900 dark:text-blue-100">{autoFillNote}</div>
                                </div>
                                <button type="button"
                                    onClick={() => setNoteValue(autoFillNote)}
                                    className="flex-shrink-0 px-2 py-1 rounded border border-blue-300 dark:border-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/40 font-semibold">
                                    {t('ack_autofill_insert') || 'Wstaw'}
                                </button>
                            </div>
                        </div>
                    )}
                    {pendingAction === 'checked' && (
                        <label className="flex gap-2 items-start text-sm mb-3 dark:text-slate-200">
                            <input type="checkbox" checked={pendingScope === 'permanent'} disabled={ackSubmitting}
                                onChange={e => setPendingScope(e.target.checked ? 'permanent' : 'period')} />
                            <span>{t('ack_retain_option')}</span>
                        </label>
                    )}
                    {pendingAction === 'client_followup' && <p className="text-sm mb-3 text-amber-700">{t('ack_client_followup_tooltip')}</p>}
                    <textarea
                        value={noteValue}
                        onChange={(e) => setNoteValue(e.target.value)}
                        placeholder={pendingScope === 'permanent' ? t('ack_permanent_note_placeholder') : (pendingAction === 'client_followup' ? t('ack_client_followup_note_placeholder') : t('ack_note_placeholder'))}
                        maxLength={500}
                        rows={4}
                        autoFocus
                        className={`w-full p-3 border rounded-lg bg-white dark:bg-slate-900 text-sm dark:text-white focus:ring-2 outline-none resize-none ${pendingScope === 'permanent' ? 'border-orange-300 dark:border-orange-700 focus:ring-orange-500 focus:border-orange-500' : (pendingAction === 'client_followup' ? 'border-amber-300 dark:border-amber-700 focus:ring-amber-500 focus:border-amber-500' : 'border-slate-300 dark:border-slate-600 focus:ring-copper-500 focus:border-copper-500')}`}
                    />
                    <div className="flex justify-between items-center mt-1">
                        {pendingScope === 'permanent' && noteValue.trim().length < 10 && (
                            <span className="text-xs text-orange-600 dark:text-orange-400">{t('ack_permanent_min_chars')}</span>
                        )}
                        <span className="text-xs text-slate-400 ml-auto">{noteValue.length}/500</span>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                        <button onClick={() => { setShowNoteModal(false); setPendingAction(null); setPendingScope('period'); setAutoFillSource(null); setAutoFillNote(''); }}
                            disabled={ackSubmitting}
                            className="px-4 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                            {t('cancel') || 'Avbryt'}
                        </button>
                        <button onClick={() => handleAck(pendingAction, noteValue.trim(), pendingScope)}
                            disabled={ackSubmitting || (pendingScope === 'permanent' ? noteValue.trim().length < 10 : noteValue.trim().length < 5)}
                            className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-40 disabled:cursor-not-allowed font-semibold transition-colors flex items-center gap-2 ${pendingScope === 'permanent' ? 'bg-orange-500 hover:bg-orange-600' : (pendingAction === 'client_followup' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-500 hover:bg-emerald-600')}`}>
                            <Icon name={ackSubmitting ? 'loader-2' : (pendingScope === 'permanent' ? 'clipboard-check' : (pendingAction === 'client_followup' ? 'message-square' : 'check-circle'))} className={`w-4 h-4 ${ackSubmitting ? 'animate-spin' : ''}`} /> {ackSubmitting ? (t('saving') || 'Lagrer...') : (pendingScope === 'permanent' ? t('ack_permanent_confirm') : (pendingAction === 'client_followup' ? t('ack_client_followup') : (t('ack_confirm') || t('ack_checked'))))}
                        </button>
                    </div>
                </div>
            </div>
        ), document.body)
        : null;

    return (
        <div className={`p-4 rounded-xl shadow-sm mb-3 issue-card ${styles[issue.type]}`} style={ackStyle} data-issue-id={issue.ruleId || ''} data-expanded={localExpanded ? 'true' : 'false'}>
            {/* P0 fix (2026-04-26): history flag — recurring error indicator */}
            {historyFlag && !isAcked && (
                <div className="mb-2 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
                    <span>🔁</span>
                    <span>
                        <strong>{historyFlag.lastPeriod}</strong>: {historyFlag.lastAction === 'checked' ? t('ack_history_was_checked') : getAckActionLabel(historyFlag.lastAction, 'period')}
                        {historyFlag.count > 1 && <span className="ml-1 opacity-70">({historyFlag.count}× {t('ack_history_total')})</span>}
                        {' — '}{t('ack_history_recurring_hint')}
                    </span>
                </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 justify-between items-start cursor-pointer" data-expand-toggle="true" onClick={() => setLocalExpanded(!localExpanded)}>
                <div className="flex gap-3 min-w-0 w-full sm:flex-1">
                    <Icon name={icons[issue.type]} className={`w-6 h-6 flex-shrink-0 ${titles[issue.type]}`} />
                    <div className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className={`font-bold ${titles[issue.type]}`}>{getIssueTitle(issue)}</h3>
                            {issue.category && CATEGORY_BADGE_LABELS[issue.category] && (
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CATEGORY_BADGE_COLORS[issue.category] || 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300'}`}>
                                    {t(CATEGORY_BADGE_LABELS[issue.category])}
                                </span>
                            )}
                            {/* Kontrakt karty (11.10.2026): pewność obok tytułu. */}
                            {['confirmed', 'open', 'info'].includes(issue.confidence) && issue.type !== 'success' && (
                                <span data-confidence={issue.confidence} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                    issue.confidence === 'confirmed' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200'
                                        : (issue.confidence === 'open' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300')}`}>
                                    {t('confidence_' + issue.confidence)}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-slate-800 dark:text-slate-100 mt-1 whitespace-pre-line leading-relaxed">{getIssueDesc(issue)}</p>
                        {/* Kontrakt karty: „dlaczego" i „sprawdziłem za Ciebie". Brak pól = brak sekcji (stare raporty).
                            Teksty powstają w języku biegu; przy innym języku widoku bez przeliczenia nie pokazujemy ich
                            (Codex P2, 11.10.2026: polskie nagłówki z norweską treścią). */}
                        {(!card.lang || card.lang === getCurrentLang()) && typeof card.why === 'string' && card.why && (
                            <p data-card-why="true" className="text-xs text-slate-600 dark:text-slate-300 mt-2 leading-relaxed">
                                <span className="font-semibold">{t('card_why_label')}:</span> {card.why}
                            </p>
                        )}
                        {(!card.lang || card.lang === getCurrentLang()) && Array.isArray(card.verified) && card.verified.length > 0 && (
                            <ul data-card-verified="true" className="mt-2 space-y-1">
                                <li className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t('card_verified_label')}</li>
                                {card.verified.map((line, i) => (
                                    <li key={i} className="text-xs text-slate-700 dark:text-slate-200 flex gap-2 leading-relaxed">
                                        <Icon name="check-circle" className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {(issue.action || issue.ruleId) && !['rule_not_checked', 'coverage', 'tx_skipped', 'screened_ok'].includes(issue.specialType) && (
                            <div className="mt-3 p-3 rounded-lg border border-dashed border-copper-300 dark:border-copper-700 bg-copper-50/50 dark:bg-copper-900/20">
                                <div className="flex items-start gap-2">
                                    <Icon name="wrench" className="w-4 h-4 text-copper-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <span className="text-xs font-bold text-copper-600 dark:text-copper-400 uppercase tracking-wider">{t('action_label')}</span>
                                        <p className="text-sm text-copper-800 dark:text-copper-200 mt-0.5">{getActionText(issue)}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {(hasVatTerms || (issue.details && issue.details.length > 0)) && (
                    <button className="text-xs font-semibold bg-white dark:bg-slate-800 px-3 py-1 rounded border dark:border-slate-600 hover:bg-slate-100 transition flex items-center gap-1 flex-shrink-0 self-end sm:self-start sm:ml-2">
                        {localExpanded ? t('collapse') : t('expand')} <Icon name={localExpanded ? "chevron-up" : "chevron-down"} className="w-3 h-3" />
                    </button>
                )}
            </div>

            {/* Ack buttons. Info items get an optional comment-only flow. */}
            {canAcknowledge && issue.ruleId && issue.type !== 'success' && !['rule_not_checked', 'coverage', 'tx_skipped', 'screened_ok'].includes(issue.specialType) && (
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-2 px-1">
                    {localAck ? (
                        <div className="flex flex-col gap-2 animate-fade-in-up w-full">
                            <div className="flex items-center gap-2 flex-wrap">
                            {/* P0 fix (2026-04-27): pomarańczowy Permanent badge gdy ack_scope='permanent' */}
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                localAckScope === 'permanent'
                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200'
                                    : (localAck === 'checked'
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                        : (localAck === 'client_followup'
                                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'))
                            }`}>
                                {localAckScope === 'permanent'
                                    ? '🚫 ' + t('ack_permanent_badge')
                                    : (localAck === 'checked'
                                        ? '✓ ' + t('ack_checked')
                                        : (localAck === 'client_followup' ? t('ack_client_followup') : '🔧 ' + t('ack_fixed')))}
                            </span>
                            {/* expiry badge — TYLKO dla period acks (permanent nigdy nie expiruje) */}
                            {localAckScope !== 'permanent' && ackStatus && ackStatus.expires_soon && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 font-medium" title={`Utløper om ${ackStatus.days_until_expiry} dager`}>
                                    ⏳ {ackStatus.days_until_expiry}d
                                </span>
                            )}
                            {/* P1 fix (2026-04-26): UI pokazuje notes z ack — backend ma od dawna, UI ignorowało.
                                Quick win z workflow-audit P1 (audit trail visibility). Hover = pełen tekst. */}
                            {ackStatus && ackStatus.notes && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 cursor-help inline-flex items-center gap-1 max-w-[200px] truncate" title={ackStatus.notes}>
                                    📝 {ackStatus.notes.length > 25 ? ackStatus.notes.slice(0, 25) + '…' : ackStatus.notes}
                                </span>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); handleUndoAck(); }}
                                className="text-xs text-slate-400 hover:text-red-500 transition-colors flex items-center gap-1" title={t('ack_undo')}>
                                <Icon name="undo-2" className="w-3.5 h-3.5" /> {t('ack_undo')}
                            </button>
                            </div>
                            {(ackActor || ackTimestamp || (ackStatus && ackStatus.notes)) && (
                                <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-white/80 dark:bg-slate-900/50 px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
                                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                                        <span><strong>{ackActionLabel}</strong></span>
                                        {ackActor && <span>Av: <strong>{ackActor}</strong></span>}
                                        {ackTimestamp && <span>Tid: <strong>{ackTimestamp}</strong></span>}
                                    </div>
                                    {ackStatus && ackStatus.notes && (
                                        <div className="mt-1 text-slate-600 dark:text-slate-300">
                                            <strong>{t('panel_notes') || 'Notat'}:</strong> {ackStatus.notes}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : issue.type === 'info' && issue.specialType !== 'review_signal' ? (
                        <button onClick={(e) => { e.stopPropagation(); handleAckClick('checked', 'period'); }}
                            className="text-sm px-4 py-2 rounded-lg bg-slate-500 text-white hover:bg-slate-600 font-semibold transition-all flex items-center gap-2 shadow-sm"
                            title={t('ack_info_note_tooltip')}>
                            <Icon name="info" className="w-4 h-4" /> {t('ack_info_note')}
                        </button>
                    ) : (
                        <>
                            <button onClick={(e) => { e.stopPropagation(); handleAckClick('checked', 'period'); }}
                                className="text-sm px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 font-semibold transition-all flex items-center gap-2 shadow-sm">
                                <Icon name="check-circle" className="w-4 h-4" /> {t('ack_checked')}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleAckClick('fixed', 'period'); }}
                                className="text-sm px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 font-semibold transition-all flex items-center gap-2 shadow-sm">
                                <Icon name="wrench" className="w-4 h-4" /> {t('ack_fixed')}
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleAckClick('client_followup', 'period'); }}
                                className="text-sm px-4 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 font-semibold transition-all flex items-center gap-2 shadow-sm"
                                title={t('ack_client_followup_tooltip')}>
                                <Icon name="message-square" className="w-4 h-4" /> {t('ack_client_followup')}
                            </button>
                        </>
                    )}
                </div>
            )}

            {noteModal}

            {(hasVatTerms || (issue.details && issue.details.length > 0)) && (
                <div style={localExpanded ? {} : { display: 'none' }} data-evidence="true">
                    {hasVatTerms ? (
                        <VatPeriodTable terms={issue.evidence.terms} />
                    ) : issue.specialType === 'amortization' ? (
                        <AmortizationTable rows={issue.details} />
                    ) : (
                        <EvidenceTable rows={issue.details} type={issue.source} ruleId={issue.ruleId} />
                    )}
                </div>
            )}
        </div>
    );
}

export default function IssueCard(props) {
    if (isRetiredFinding(props.issue)) return null;
    return <ReportIssueCard {...props} />;
}
